import Loader from '@/components/common/Loader/Loader';
import { showToast } from '@/components/ui/sonner';
import ZoomMediaContext, { MediaContext } from '@/context/media-context';
import ZoomContext from '@/context/zoom-context';
import { MediaStream } from '@/Types/zoom';
import { authState } from '@/vars/auth';
import { useMutation, useReactiveVar } from '@apollo/client';
import ZoomVideo, { ConnectionState, ReconnectReason } from '@zoom/videosdk';
import { produce } from 'immer';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import PreSession from './components/pre-session';
import VideoContainer from './components/video';
import { CREATE_ZOOM_TOKEN } from './GraphQl/mutation';
import { activeMediaDeviceVar, miniScreenUserIdVar, preSessionVar, sessionVar } from './meetingVar';
interface AppProps {
  meetingArgs: {
    sdkKey: string;
    topic: string;
    signature: string;
    name: string;
    password?: string;
    webEndpoint?: string;
    enforceGalleryView?: string;
    enforceVB?: string;
    customerJoinId?: string;
    lang?: string;
    useVideoPlayer?: string;
  };
}

const mediaShape = {
  audio: {
    encode: false,
    decode: false,
  },
  video: {
    encode: false,
    decode: false,
  },
  share: {
    encode: false,
    decode: false,
  },
};

const mediaReducer = produce((draft, action) => {
  switch (action.type) {
    case 'audio-encode': {
      draft.audio.encode = action.payload;
      break;
    }
    case 'audio-decode': {
      draft.audio.decode = action.payload;
      break;
    }
    case 'video-encode': {
      draft.video.encode = action.payload;
      break;
    }
    case 'video-decode': {
      draft.video.decode = action.payload;
      break;
    }
    case 'share-encode': {
      draft.share.encode = action.payload;
      break;
    }
    case 'share-decode': {
      draft.share.decode = action.payload;
      break;
    }
    case 'reset-media': {
      Object.assign(draft, { ...mediaShape });
      break;
    }
    default:
      break;
  }
}, mediaShape);

export default function ZoomMtg(props: AppProps) {
  const {
    meetingArgs: { enforceGalleryView, enforceVB, customerJoinId, lang },
  } = props;
  const { id } = useParams();
  const sessionData = useReactiveVar(sessionVar);
  const { userDetails } = useReactiveVar(authState);
  const isPreSessionComplete = useReactiveVar(preSessionVar);
  const [presessionLoading, setPresessionLoading] = useState(false);
  const [preAudio, setPreAudio] = useState(false);
  const [preVideo, setPreVideo] = useState(false);
  const [loading, setIsLoading] = useState(true);
  const [loadingText, setLoadingText] = useState('');
  const [isFailover, setIsFailover] = useState<boolean>(false);
  const [, setStatus] = useState<string>('closed');
  const [mediaState, dispatch] = useReducer(mediaReducer, mediaShape);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const zmClient = useContext(ZoomContext);
  const mediaContext = useMemo(() => ({ ...mediaState, mediaStream }), [mediaState, mediaStream]);
  const galleryViewWithoutSAB = Number(enforceGalleryView) === 1 && !window.crossOriginIsolated;
  const vbWithoutSAB = Number(enforceVB) === 1 && !window.crossOriginIsolated;
  const { camera, mic, speaker } = useReactiveVar(activeMediaDeviceVar);

  const waitRef = useRef(0);

  const [createToken] = useMutation(CREATE_ZOOM_TOKEN, { fetchPolicy: 'network-only' });

  useEffect(() => {
    const init = async () => {
      await zmClient.init('en-US', 'Global', {
        stayAwake: true,
        patchJsMedia: true,
        enforceMultipleVideos: true,
        leaveOnPageUnload: true,
      });
      try {
        const stream = zmClient.getMediaStream();
        setMediaStream(stream);
        setIsLoading(false);
      } catch (e: any) {
        setIsLoading(false);
        console.log('error', e.reason);
      }
    };
    init();
    return () => {
      ZoomVideo.destroyClient();
    };
  }, [zmClient, galleryViewWithoutSAB, customerJoinId, lang, vbWithoutSAB]);

  const onMediaSDKChange = useCallback((payload: any) => {
    const { action, type, result } = payload;
    dispatch({ type: `${type}-${action}`, payload: result === 'success' });
  }, []);

  const onConnectionChange = useCallback(
    (payload: any) => {
      if (payload.state === ConnectionState.Reconnecting) {
        setIsLoading(true);
        setIsFailover(true);
        setStatus('connecting');
        const { reason, subsessionName } = payload;
        if (reason === ReconnectReason.Failover) {
          setLoadingText('Session Disconnected,Try to reconnect');
        } else if (
          reason === ReconnectReason.JoinSubsession ||
          reason === ReconnectReason.MoveToSubsession
        ) {
          setLoadingText(`Joining ${subsessionName}...`);
        } else if (reason === ReconnectReason.BackToMainSession) {
          setLoadingText('Returning to Main Session...');
        }
      } else if (payload.state === ConnectionState.Connected) {
        setStatus('connected');
        if (isFailover) {
          setIsLoading(false);
        }
      } else if (payload.state === ConnectionState.Closed) {
        setStatus('closed');
        dispatch({ type: 'reset-media' });
        if (payload.reason === 'ended by host') {
          console.log('This meeting has been ended by host');
        }
      }
    },
    [isFailover, zmClient],
  );

  useEffect(() => {
    zmClient.on('connection-change', onConnectionChange);
    zmClient.on('media-sdk-change', onMediaSDKChange);
    return () => {
      zmClient.off('connection-change', onConnectionChange);
      zmClient.off('media-sdk-change', onMediaSDKChange);
    };
  }, [zmClient, onConnectionChange, onMediaSDKChange]);

  const handleJoin = async () => {
    const res = await createToken({
      variables: {
        data: {
          sessionId: id || '',
          role: 0,
        },
      },
    });
    if (!res?.data?.createZoomToken?.status) {
      if (waitRef.current === 2) {
        showToast({
          message:
            'You are currently in an active session. Please end it on the active device and try again later. ',
          variant: 'error',
        });
      } else {
        waitRef.current = waitRef.current + 1;
        showToast({
          message: 'Unable to join the meeting room. Retrying in 3 seconds...',
          variant: 'warning',
        });
        setTimeout(() => {
          handleJoin();
        }, 3000);
      }
    } else {
      waitRef.current = 0;
      if (res.data.createZoomToken.token && sessionData?._id) {
        setLoadingText('Joining the session...');
        setPresessionLoading(true);
        await zmClient
          .join(
            sessionData?._id,
            res.data.createZoomToken.token,
            userDetails?.firstName ?? '' + userDetails?.lastName ?? '',
          )
          .then(() => console.log('join success'))
          .catch((err) => console.log('err', err));
        await mediaStream
          ?.startAudio({ microphoneId: mic, speakerId: speaker })
          .then(() => console.log('start audio success'))
          .catch((err) => console.log('Failed to start audio', err));
        if (preAudio) {
          await mediaStream?.unmuteAudio().catch((err) => console.log('err', err));
        } else {
          await mediaStream?.muteAudio().catch((err) => console.log('err', err));
        }
        if (preVideo) {
          await mediaStream?.startVideo({ cameraId: camera });
        }
        preSessionVar(true);
        setPresessionLoading(false);
        miniScreenUserIdVar(zmClient.getCurrentUserInfo()?.userId);
      } else {
        console.log('Error joining session, token not found');
        showToast({ message: 'Error joining session', variant: 'error' });
      }
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isPreSessionComplete) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPreSessionComplete]);

  return (
    <>
      {loading ? (
        <div className='relative'>
          <Loader className='bg-black h-[calc(100vh-73px)]' />
          <h4 className='absolute w-full h-full top-0 flex items-center justify-center pt-20'>
            {loadingText}
          </h4>
        </div>
      ) : (
        <ZoomMediaContext.Provider value={mediaContext as MediaContext}>
          <div className='relative'>
            {!isPreSessionComplete && (
              <div className='absolute top-0 left-0 w-full h-full z-50'>
                <PreSession
                  isAudioOn={preAudio}
                  isVideoOn={preVideo}
                  setIsAudioOn={setPreAudio}
                  setIsVideoOn={setPreVideo}
                  handleJoin={handleJoin}
                  loading={presessionLoading}
                />
              </div>
            )}
            <VideoContainer />
          </div>
        </ZoomMediaContext.Provider>
      )}
    </>
  );
}
